import StorageService from './storage.service';

/**
 * This is a ts->js copy of web/typo3conf/ext/messe_xpospherelive/src/Utilities/TrackingUtility.ts
 * Never update this file independently. If there are changes required, make them in the original file and copy them here.
 */
export class TrackingUtility {
  config = {
    trackingDomain: null,
    siteId: null,
    enableHeartBeatTimer: false,
    trackPageView: false,
    enableLinkTracking: false,
  };
  fairEventUid;
  lmsUser = null;
  tracked = [];

  /**
   * @param {object} config
   * @param {string} config.trackingDomain
   * @param {string} config.siteId
   * @param {boolean} config.enableHeartBeatTimer
   * @param {boolean} config.trackPageView
   * @param {boolean} config.enableLinkTracking
   * @param {string} fairEventUid
   */
  constructor(config, fairEventUid) {
    if (!config.trackingDomain || !config.siteId) {
      throw new Error('Tracking config invalid.');
    }
    this.config = config;
    this.fairEventUid = fairEventUid;
    this.initGlobalVar();
  }

  async init() {
    if (this.isTrackingAllowed() && !this.alreadyLoaded()) {
      /* tracker methods like "setCustomDimension" should be called before "trackPageView" */
      if (this.config.enableHeartBeatTimer) {
        window._paq.push(['enableHeartBeatTimer']);
      }
      if (this.config.trackPageView) {
        window._paq.push(['trackPageView']);
      }
      if (this.config.enableLinkTracking) {
        window._paq.push(['enableLinkTracking']);
      }
      await this.loadScript();
    }
  }

  /**
   * Allows setting a meaningful ID for the visitor profile in matomo.
   * See https://matomo.org/faq/reports/set-up-user-id-tracking-in-matomo/
   * @param {number} userId
   */
  setUserId(userId) {
    if (this.alreadyActive() && this.isTrackingAllowed() && !this.configuredUserId()) {
      window._paq.push(['setUserId', `${ userId }`]);
      StorageService.setItem('trackingUserId', `${ userId }`);
    }
  }

  /**
   * Using events for tracking whether a user watched given exhibitors
   * @param {number} userId
   * @param {Array<number>} exhibitorIds
   */
  setExhibitorIds(userId, exhibitorIds) {
    if (this.alreadyActive() && this.isTrackingAllowed()) {
      exhibitorIds.forEach(id => {
          if (!this.tracked.includes(id)) {
            window._paq.push(['trackEvent', 'Exhibitor', 'View', `${ userId }`, id, {dimension1: id}]);
            this.tracked.push(id);
          }
        },
      );
    }
  }

  async loadScript() {
    const u = `//${ this.config.trackingDomain }/`;
    window._paq.push(['setTrackerUrl', u + 'matomo.php']);
    window._paq.push(['setSiteId', this.config.siteId]);
    const d = document, g = d.createElement('script'), s = d.getElementsByTagName('script')[0];
    g.async = true;
    g.src = u + 'matomo.js';
    if (s.parentNode) {
      s.parentNode.insertBefore(g, s);
    }
    StorageService.setItem('trackingLoaded', '1');
  }

  alreadyActive() {
    return Boolean(window._paq);
  }

  initGlobalVar() {
    if (this.alreadyActive()) {
      return window._paq;
    }
    window._paq = window._paq || [];
  }

  /**
   * Whether the user has agreed to tracking his activity on the xposphere part of the website
   * @returns {boolean}
   */
  isTrackingAllowed() {
    const lmsUser = this.getLmsUser();
    if (lmsUser && lmsUser.accountCode && this.hasTrackingConfigured()) {
      return this.allowsTracking();
    }
    return false;
  }

  /**
   * On false: First time visit. Never said no or yes to this fair event.
   * @returns {boolean}
   */
  hasTrackingConfigured() {
    const lmsUser = this.getLmsUser();
    if (lmsUser && lmsUser.accountCode) {
      return this.getPermission(this.fairEventUid) !== null;
    }
    return false;
  }

  allowsTracking() {
    return this.getLmsUser().permissions.find(p => p.fairEvent.id === this.fairEventUid && p.active) !== undefined;
  }

  getPermission() {
    return this.getLmsUser().permissions.find(p => p.fairEvent.id === this.fairEventUid) || null;
  }

  alreadyLoaded() {
    return StorageService.getItem('trackingLoaded') === '1';
  }

  configuredUserId() {
    return StorageService.getItem('trackingUserId') !== null;
  }

  clearStorage() {
    StorageService.removeItem('trackingLoaded');
    StorageService.removeItem('trackingUserId');
  }

  getLmsUser() {
    if (this.lmsUser) {
      return this.lmsUser;
    }
    const lmsUserData = StorageService.getItem('buyerData');
    if (lmsUserData) {
      this.lmsUser = JSON.parse(lmsUserData);
      return this.lmsUser;
    }
    return null;
  }
}
